'use client';

import { useCallback, useMemo, useRef } from 'react';
import { useTranslations } from 'next-intl';
import {
  ConversationHistoryPanel as ConversationHistoryPanelBase,
  ConversationHistoryTrigger as ConversationHistoryTriggerBase,
  toasterApi,
  type ConversationHistoryDraftEntry,
  type ConversationHistoryLabels,
  type ConversationHistorySavedEntry,
} from '@terra/ui-design-system';
import { useMentions } from '../hooks/useMentions';
import type { ConversationCreatedConversation } from '@terra/shared-dto-chat-types';
import { useConversations } from '../store/conversations-store';
import { useTargetType, useOptionalTargetId } from '../store/projectStore';
import { useChatTabs } from '../store/chatTabsStore';
import { switchToChatTab } from './chat/actions/chatActions';
import { useActiveChatProgress } from '../hooks/chat/useActiveChatProgress';
import { useChatInstancesStore } from '../store/chatInstancesStore';
import {
  hasDraftChatTabContent,
  resolveChatTabPreviewText,
} from './chat/chatTabDisplayTitle';
import { useDateFormatter } from '../hooks/useDateFormatter';
import {
  conversationTabTargetFields,
  isTenantScopedConversation,
} from './conversation-history-scope';

function useConversationHistoryLabels(): ConversationHistoryLabels {
  const tCopilot = useTranslations('chat.copilot');
  const tCommon = useTranslations('common');
  const dateFormatter = useDateFormatter();

  return useMemo(
    () => ({
      triggerAriaLabel: tCopilot('historyTriggerAriaLabel'),
      panelTitle: tCopilot('historyPanelTitle'),
      closePanelAriaLabel: tCommon('close'),
      searchPlaceholder: tCopilot('historySearchPlaceholder'),
      empty: tCopilot('historyEmpty'),
      noResults: tCopilot('historyNoResults'),
      draftsSection: tCopilot('draftHistorySection'),
      draftTitle: (preview: string) => tCopilot('draftTitle', { preview }),
      closeDraftAriaLabel: tCopilot('closeDraftChatLabel'),
      deleteConversationAriaLabel: tCopilot('deleteConversationAriaLabel'),
      deleteConversationConfirmTitle: tCopilot(
        'deleteConversationConfirmTitle',
      ),
      deleteConversationConfirmDescription: tCopilot(
        'deleteConversationConfirmDescription',
      ),
      deleteConfirmLabel: tCommon('delete'),
      deleteLabel: tCommon('delete'),
      renameLabel: tCommon('rename'),
      moreActionsAriaLabel: tCopilot('moreActionsAriaLabel'),
      untitledConversation: tCopilot('untitledConversation'),
      dateGroups: {
        today: tCopilot('historyDateGroups.today'),
        yesterday: tCopilot('historyDateGroups.yesterday'),
        last7Days: tCopilot('historyDateGroups.last7Days'),
        last30Days: tCopilot('historyDateGroups.last30Days'),
        older: tCopilot('historyDateGroups.older'),
      },
      formatUpdatedAt: (isoString: string) =>
        dateFormatter.formatCompactRelativeTime(isoString),
    }),
    [dateFormatter, tCommon, tCopilot],
  );
}

export function useConversationHistoryData() {
  const {
    chatTabs,
    closeTab,
    setActiveChatTabId,
    addChatTab,
    updateChatTab,
    activeChatTabId,
  } = useChatTabs();
  const { activeChatIds, hasActiveChats } = useActiveChatProgress();
  const tCopilot = useTranslations('chat.copilot');
  const labels = useConversationHistoryLabels();

  const targetId = useOptionalTargetId();
  const targetType = useTargetType();

  const {
    conversations,
    activeConversationId,
    deleteConversation,
    updateConversation,
    getConversation,
    setActiveConversation,
  } = useConversations({ targetId });

  const { clearMentions } = useMentions();

  useChatInstancesStore((state) =>
    chatTabs
      .filter((tab) => !tab.conversationId)
      .map((tab) => {
        const chat = state.chats.get(tab.chatId);
        const queuedCount = state.getQueuedMessages({
          chatId: tab.chatId,
        }).length;

        return `${tab.tabId}:${tab.draftInput ?? ''}:${chat?.messages.length ?? 0}:${queuedCount}`;
      })
      .join('|'),
  );

  const draftTabs = chatTabs.filter((tab) => hasDraftChatTabContent(tab));
  const draftUpdatedAtByTabIdRef = useRef(
    new Map<string, { contentKey: string; updatedAt: string }>(),
  );

  const drafts = useMemo<ConversationHistoryDraftEntry[]>(() => {
    const previousByTabId = draftUpdatedAtByTabIdRef.current;
    const nextTouchedAt = new Map<
      string,
      { contentKey: string; updatedAt: string }
    >();

    const nextDrafts = draftTabs.flatMap((tab) => {
      const preview = resolveChatTabPreviewText(tab);

      if (!preview) {
        return [];
      }

      const chat = useChatInstancesStore.getState().chats.get(tab.chatId);
      const queuedCount = useChatInstancesStore
        .getState()
        .getQueuedMessages({ chatId: tab.chatId }).length;
      const contentKey = `${preview}\0${tab.draftInput ?? ''}\0${chat?.messages.length ?? 0}\0${queuedCount}`;
      const previous = previousByTabId.get(tab.tabId);
      const updatedAt =
        previous?.contentKey === contentKey
          ? previous.updatedAt
          : new Date().toISOString();

      nextTouchedAt.set(tab.tabId, { contentKey, updatedAt });

      const isActiveDraftTab = activeChatTabId === tab.tabId;
      const isDraftStreaming =
        activeChatIds.has(tab.chatId) && !isActiveDraftTab;

      return [
        {
          kind: 'draft' as const,
          id: tab.tabId,
          title: tab.title,
          subtitle: preview,
          updatedAt,
          isStreaming: isDraftStreaming,
        },
      ];
    });

    draftUpdatedAtByTabIdRef.current = nextTouchedAt;

    return nextDrafts;
  }, [activeChatIds, activeChatTabId, draftTabs]);

  const savedConversations = useMemo<ConversationHistorySavedEntry[]>(
    () =>
      (conversations ?? [])
        .filter(
          (conversation: ConversationCreatedConversation) =>
            Boolean(targetId) || isTenantScopedConversation(conversation),
        )
        .map((conversation: ConversationCreatedConversation) => ({
          kind: 'conversation' as const,
          id: conversation.id,
          title: conversation.title ?? tCopilot('newChatTitle'),
          description: conversation.description,
          updatedAt: conversation.updatedAt,
          isStreaming: activeChatIds.has(conversation.threadId),
        })),
    [activeChatIds, conversations, tCopilot, targetId],
  );

  const onLoadConversation = async (conversationId: string) => {
    const conversation = getConversation(conversationId);

    if (!conversation) {
      return;
    }

    const existingTab = chatTabs.find(
      (tab) => tab.conversationId === conversationId,
    );

    if (existingTab) {
      switchToChatTab({ tabId: existingTab.tabId });

      return;
    }

    const newTabId = conversation.threadId;
    const newTab = {
      tabId: newTabId,
      chatId: conversation.threadId,
      conversationId,
      title: conversation.title || tCopilot('newChatTitle'),
      ...conversationTabTargetFields({
        conversation,
        currentTargetId: targetId,
        currentTargetType: targetType,
      }),
      mentions: [],
    };

    addChatTab(newTab);
    setActiveChatTabId(newTabId);
    setActiveConversation(conversationId);
    clearMentions();
  };

  const onDeleteConversation = (conversationId: string) => {
    deleteConversation(conversationId);

    const conversationTab = chatTabs.find(
      (tab) => tab.conversationId === conversationId,
    );

    if (conversationTab) {
      closeTab({ tabId: conversationTab.tabId });
    }
  };

  const onRenameConversation = useCallback(
    async (conversationId: string, title: string) => {
      try {
        const updated = await updateConversation({
          conversationId,
          title,
        });

        const conversationTab = chatTabs.find(
          (tab) => tab.conversationId === conversationId,
        );

        if (conversationTab) {
          updateChatTab(conversationTab.tabId, {
            title: updated.title || title,
            hasCustomTitle: true,
          });
        }
      } catch (error: unknown) {
        toasterApi.error({
          title: tCopilot('renameConversationFailed'),
          description:
            error instanceof Error && error.message ? error.message : undefined,
        });

        throw error;
      }
    },
    [chatTabs, tCopilot, updateChatTab, updateConversation],
  );

  return {
    labels,
    drafts,
    savedConversations,
    activeId: activeConversationId ?? activeChatTabId,
    hasActiveChats,
    onSelect: (id: string, kind: 'draft' | 'conversation') => {
      if (kind === 'draft') {
        switchToChatTab({ tabId: id });

        return;
      }

      void onLoadConversation(id);
    },
    onDelete: (id: string, kind: 'draft' | 'conversation') => {
      if (kind === 'draft') {
        closeTab({ tabId: id });

        return;
      }

      onDeleteConversation(id);
    },
    onRename: onRenameConversation,
  };
}

export function ConversationHistoryTrigger({
  isOpen,
  onOpenChange,
  labels,
  hasActiveChats,
}: {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  labels: ConversationHistoryLabels;
  hasActiveChats: boolean;
}) {
  return (
    <ConversationHistoryTriggerBase
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      labels={labels}
      showStreamingIndicator={hasActiveChats}
      triggerVariant="link"
    />
  );
}

export function ConversationHistoryPanel({
  isOpen,
  onClose,
  labels,
  drafts,
  savedConversations,
  activeId,
  onSelect,
  onDelete,
  onRename,
}: {
  isOpen: boolean;
  onClose: () => void;
  labels: ConversationHistoryLabels;
  drafts: ConversationHistoryDraftEntry[];
  savedConversations: ConversationHistorySavedEntry[];
  activeId: string | null;
  onSelect: (id: string, kind: 'draft' | 'conversation') => void;
  onDelete: (id: string, kind: 'draft' | 'conversation') => void;
  onRename: (id: string, title: string) => void | Promise<void>;
}) {
  return (
    <ConversationHistoryPanelBase
      isOpen={isOpen}
      onClose={onClose}
      drafts={drafts}
      conversations={savedConversations}
      activeId={activeId}
      labels={labels}
      onSelect={onSelect}
      onDelete={onDelete}
      onRename={onRename}
    />
  );
}
